import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import compose from 'recompose/compose'
import { connect as connectRedux } from 'react-redux'
import { useLazyQuery } from '@apollo/client'
import { fromJS, Map } from 'immutable'
import _get from 'lodash/get'
import { getBoundActions } from '../../../actions/index'
import Button, { TYPES } from '../../Button/index'
import ArrowRight from '../../Icon/icons/ArrowRight/index'
import Sherpa, { TYPE_LARGE } from '../../Sherpa/index'
import {
  CONTENT_IMAGE_LIST,
  createTitleGroupId,
  getFormattedContentData,
} from '../../../services/content/index'
import { GET_RELATED } from '../../../services/content/query'
import { getPrimary as getPrimaryLanguage } from '../../../services/languages/index'
import GaiaLogo, { TYPE_TEAL } from '../../GaiaLogo/index'
import OnboardingAddToPlaylistTile from './OnboardingAddToPlaylistTile'

const RELATED_LIMIT = 8

/**
 * Playlist suggestion step: content-impression title plus related titles from GraphQL.
 * Selectable tiles and one CTA that adds selections to the default (Watch Later) playlist.
 */
function OnboardingAddToPlaylist (props) {
  const {
    auth,
    user,
    inboundTracking,
    staticText,
    batchPlaylistItems,
    sendOptimizelyFeatureEvent,
    onContinueToFinal,
  } = props

  const jwt = auth.get('jwt')
  const userLanguage = getPrimaryLanguage(user.getIn(['data', 'language']))

  const [selectedIds, setSelectedIds] = useState(() => new Set())

  const ciId = Map.isMap(inboundTracking)
    ? inboundTracking.getIn(['data', 'ci_id'])
    : null
  const ciType = Map.isMap(inboundTracking)
    ? inboundTracking.getIn(['data', 'ci_type'])
    : null

  const titleGroupId = useMemo(
    () => (ciId && ciType ? createTitleGroupId(ciId, ciType) : ''),
    [ciId, ciType],
  )

  const [fetchPlaylist, { data, loading: queryLoading }] = useLazyQuery(
    GET_RELATED,
    {
      context: { jwt },
    },
  )

  useEffect(() => {
    if (!titleGroupId || !jwt) {
      return undefined
    }
    fetchPlaylist({
      variables: {
        titleGroupIds: [titleGroupId],
        language: userLanguage,
        imageType: CONTENT_IMAGE_LIST,
        limit: RELATED_LIMIT,
      },
    })
    return undefined
  }, [titleGroupId, jwt, userLanguage, fetchPlaylist])

  const tiles = useMemo(() => {
    const contentRelated = _get(data, ['contentList', 'content', 0, 'contentRelated'])
    if (!contentRelated) {
      return []
    }

    return contentRelated.map((item) => getFormattedContentData(fromJS(item)))
  }, [data])

  useEffect(() => {
    setSelectedIds(new Set())
  }, [titleGroupId])

  const toggleTile = useCallback((contentId) => {
    if (contentId == null) {
      return
    }
    setSelectedIds((prev) => {
      const next = new Set(prev)
      const idNum = Number(contentId)
      if (next.has(idNum)) {
        next.delete(idNum)
      } else {
        next.add(idNum)
      }
      return next
    })
  }, [])

  const handleContinue = useCallback(() => {
    if (selectedIds.size > 0) {
      batchPlaylistItems({
        ids: Array.from(selectedIds),
        type: 'default',
        add: true,
      })
    }
    sendOptimizelyFeatureEvent({ eventName: 'onboarding_add_to_playlist' })
    onContinueToFinal()
  }, [batchPlaylistItems, onContinueToFinal, selectedIds, sendOptimizelyFeatureEvent])

  const loading = queryLoading && !data
  const showEmpty = !loading && tiles.length === 0

  return (
    <div className="onboarding-add-to-playlist">
      <div className={`onboarding__launch ${userLanguage}`}>
        <div className="onboarding__launch__container">
          <div className="onboarding__gradient-bar" />
          <div className="onboarding-add-to-playlist__content">
            <div className="onboarding-add-to-playlist__header">
              <div className="onboarding__logo-container">
                <GaiaLogo
                  type={TYPE_TEAL}
                  className={['onboarding__logo']}
                />
              </div>
              <Button
                type={TYPES.LINK}
                onClick={onContinueToFinal}
                className="onboarding-add-to-playlist__skip-button"
              >
                {staticText.get('skip')}
                <ArrowRight />
              </Button>
            </div>
            <p className="onboarding-add-to-playlist__lead onboarding-add-to-playlist__lead--mobile">
              {staticText.get('addToPlaylistMobileLead')}
            </p>
            <p className="onboarding-add-to-playlist__lead onboarding-add-to-playlist__lead--desktop">
              {staticText.get('addToPlaylistStepTitle')}
            </p>
            <div className="onboarding-add-to-playlist__grid-container">
              {loading ? (
                <div className="onboarding-add-to-playlist__loading">
                  <Sherpa type={TYPE_LARGE} />
                </div>
              ) : null}
              {showEmpty ? (
                <p className="onboarding-add-to-playlist__empty">
                  {staticText.get('addToPlaylistStepEmpty')}
                </p>
              ) : null}
              {!loading && tiles.length > 0 ? (
                <ul className="onboarding-add-to-playlist__grid">
                  {tiles.map((tile) => {
                    const tileKey = tile.get('titleGroupId')

                    return (
                      <li key={tileKey}>
                        <OnboardingAddToPlaylistTile
                          tile={tile}
                          isSelected={selectedIds.has(Number(tile.get('contentId')))}
                          onToggleSelected={toggleTile}
                        />
                      </li>
                    )
                  })}
                </ul>
              ) : null}
              {selectedIds.size > 0 ? (
                <Button
                  type={TYPES.PRIMARY}
                  onClick={handleContinue}
                  className="onboarding-add-to-playlist__cta"
                >
                  {staticText.get('addToPlaylistContinue')}
                </Button>
              ) : (
                <Button
                  type={TYPES.SECONDARY}
                  onClick={onContinueToFinal}
                  className="onboarding-add-to-playlist__cta"
                >
                  {staticText.get('continue')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

OnboardingAddToPlaylist.propTypes = {
  auth: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  inboundTracking: ImmutablePropTypes.map,
  copy: ImmutablePropTypes.map.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
  screenNumber: PropTypes.number.isRequired,
  batchPlaylistItems: PropTypes.func.isRequired,
  onContinueToFinal: PropTypes.func.isRequired,
  sendOptimizelyFeatureEvent: PropTypes.func.isRequired,
}

export default compose(
  connectRedux(
    (state) => ({
      auth: state.auth,
      user: state.user,
      inboundTracking: state.inboundTracking,
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        batchPlaylistItems: actions.playlist.batchPlaylistItems,
        sendOptimizelyFeatureEvent: actions.optimizelyFeature.sendOptimizelyFeatureEvent,
      }
    },
  ),
)(OnboardingAddToPlaylist)
